import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Prestamo } from '../../types';
import { usePrestamos } from '../../context/PrestamosContext';
import { X, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { useCerrarConEscape } from '../../hooks/useCerrarConEscape';

interface Props {
  prestamo?: Prestamo;
  onClose: () => void;
}

const claseInput =
  'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 ' +
  'text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent';

const Campo: React.FC<{ label: string; children: React.ReactNode; ayuda?: string }> =
  ({ label, children, ayuda }) => (
    <div className="flex flex-col gap-1 min-w-0">
      <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">{label}</label>
      {children}
      {ayuda && <p className="text-[11px] text-gray-500 dark:text-gray-500">{ayuda}</p>}
    </div>
  );

const aInput = (d?: Date) => (d ? format(d, 'yyyy-MM-dd') : '');
// Mediodía local, para que el cambio de huso no la mueva de día
const deInput = (s: string) => (s ? new Date(`${s}T12:00:00`) : undefined);

export const ModalPrestamo: React.FC<Props> = ({ prestamo, onClose }) => {
  useCerrarConEscape(onClose);
  const { prestamos, crearPrestamo, actualizarPrestamo } = usePrestamos();

  const [f, setF] = useState({
    direccion: prestamo?.direccion ?? ('prestado' as Prestamo['direccion']),
    objeto: prestamo?.objeto ?? '',
    persona: prestamo?.persona ?? '',
    fechaPrestamo: aInput(prestamo?.fechaPrestamo ?? new Date()),
    fechaPrevista: aInput(prestamo?.fechaPrevista),
    fechaDevolucion: aInput(prestamo?.fechaDevolucion),
    notas: prestamo?.notas ?? '',
  });
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const set = (k: keyof typeof f, v: string) => setF(prev => ({ ...prev, [k]: v }));

  // Personas ya usadas, para sugerirlas al escribir
  const personasConocidas = useMemo(() => {
    const vistas = new Map<string, string>();
    prestamos.forEach(p => {
      const n = p.persona.trim();
      if (n && !vistas.has(n.toLowerCase())) vistas.set(n.toLowerCase(), n);
    });
    return Array.from(vistas.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [prestamos]);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.objeto.trim()) { setError('Indica qué objeto es.'); return; }
    if (!f.persona.trim()) { setError('Indica la persona.'); return; }
    if (!f.fechaPrestamo) { setError('Indica la fecha del préstamo.'); return; }
    if (f.fechaPrevista && f.fechaPrevista < f.fechaPrestamo) {
      setError('La fecha prevista de vuelta no puede ser anterior al préstamo.'); return;
    }
    if (f.fechaDevolucion && f.fechaDevolucion < f.fechaPrestamo) {
      setError('La devolución no puede ser anterior al préstamo.'); return;
    }

    setGuardando(true);
    setError('');
    const datos = {
      direccion: f.direccion,
      objeto: f.objeto.trim(),
      persona: f.persona.trim(),
      fechaPrestamo: deInput(f.fechaPrestamo)!,
      fechaPrevista: deInput(f.fechaPrevista),
      fechaDevolucion: deInput(f.fechaDevolucion),
      notas: f.notas.trim() || undefined,
    };

    try {
      if (prestamo) await actualizarPrestamo(prestamo.id, datos);
      else await crearPrestamo(datos);
      onClose();
    } catch (err) {
      console.error(err);
      setError('No se pudo guardar. Inténtalo de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const prestado = f.direccion === 'prestado';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center capa-hoja"
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="hoja bg-white dark:bg-gray-800 w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl
                   max-h-full overflow-y-auto shadow-2xl">

        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700
                        px-5 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {prestamo ? 'Editar préstamo' : 'Nuevo préstamo'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Cerrar"
            className="boton-cerrar text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                       hover:bg-gray-100 dark:hover:bg-gray-700 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={enviar} className="p-5 space-y-4">

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
                            text-red-700 dark:text-red-300 rounded-lg px-3 py-2 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            {([
              { v: 'prestado', label: 'Lo dejo yo', Icon: ArrowUpRight },
              { v: 'recibido', label: 'Me lo dejan', Icon: ArrowDownLeft },
            ] as const).map(o => (
              <button key={o.v} type="button" onClick={() => set('direccion', o.v)}
                aria-pressed={f.direccion === o.v}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-semibold transition ${
                  f.direccion === o.v
                    ? 'bg-cyan-600 border-cyan-600 text-white'
                    : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-400'
                }`}>
                <o.Icon className="w-4 h-4" /> {o.label}
              </button>
            ))}
          </div>

          <Campo label="Objeto *">
            <input type="text" value={f.objeto} onChange={e => set('objeto', e.target.value)}
              placeholder="Taladro, libro «Dune», tienda de campaña…" className={claseInput} autoFocus />
          </Campo>

          <Campo label={prestado ? 'A quién se lo dejas *' : 'Quién te lo deja *'}>
            <input type="text" value={f.persona} onChange={e => set('persona', e.target.value)}
              list="personas-prestamo" placeholder="Nombre" className={claseInput} />
            <datalist id="personas-prestamo">
              {personasConocidas.map(p => <option key={p} value={p} />)}
            </datalist>
          </Campo>

          <div className="grid gap-3 grid-cols-2">
            <Campo label="Fecha del préstamo *">
              <input type="date" value={f.fechaPrestamo} onChange={e => set('fechaPrestamo', e.target.value)}
                className={claseInput} />
            </Campo>
            <Campo label="Vuelve antes del" ayuda="Opcional. Avisa si se pasa.">
              <input type="date" value={f.fechaPrevista} onChange={e => set('fechaPrevista', e.target.value)}
                className={claseInput} />
            </Campo>
          </div>

          {prestamo && (
            <Campo label="Devuelto el" ayuda="Déjalo vacío si todavía no ha vuelto.">
              <input type="date" value={f.fechaDevolucion} onChange={e => set('fechaDevolucion', e.target.value)}
                className={claseInput} />
            </Campo>
          )}

          <Campo label="Notas" ayuda="Estado en que salió, accesorios, lo que convenga recordar.">
            <textarea value={f.notas} onChange={e => set('notas', e.target.value)} rows={3}
              placeholder="Va con el maletín y dos brocas." className={`${claseInput} resize-y`} />
          </Campo>

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={guardando}
              className="flex-1 py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white
                         text-sm font-semibold transition disabled:opacity-60">
              {guardando ? 'Guardando…' : prestamo ? 'Guardar cambios' : 'Anotar préstamo'}
            </button>
            <button type="button" onClick={onClose}
              className="px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600
                         text-gray-700 dark:text-gray-300 text-sm font-semibold
                         hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
